import { useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { PORTFOLIO_STALE_TIME } from "@/api/portfolio-query";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useMetricsOverview = (
  timePeriod: "daily" | "weekly" | "monthly" = "weekly",
  dateRange?: { from: string; to: string },
) => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: ["metrics-overview", scope.merchantId, scope.workspaceId, timePeriod, dateRange],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].metrics.overview.$get({
            param: scope.param,
            query: {
              timePeriod,
              ...(dateRange && { from: dateRange.from, to: dateRange.to }),
            },
          })
        : await client.merchant[":merchantId"].metrics.overview.$get({
            param: scope.param,
            query: {
              timePeriod,
              ...(dateRange && { from: dateRange.from, to: dateRange.to }),
            },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const usePortfolioMetricsOverview = (
  timePeriod: "daily" | "weekly" | "monthly" = "weekly",
  dateRange?: { from: string; to: string },
  workspaceId?: string,
) => {
  return useQuery({
    queryKey: ["portfolio-metrics-overview", workspaceId ?? null, timePeriod, dateRange],
    queryFn: async () => {
      if (!dateRange) throw new Error("Portfolio metrics require a date range");
      const query = { timePeriod, from: dateRange.from, to: dateRange.to };
      const res = workspaceId
        ? await client.workspace[":workspaceId"].portfolio["metrics-overview"].$get({
            param: { workspaceId },
            query,
          })
        : await client.portfolio["metrics-overview"].$get({ query });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!dateRange,
    staleTime: PORTFOLIO_STALE_TIME,
  });
};
